import { create } from "zustand";
import type { ICompany, IIVerificationLocation, IParams, IProduct, IScanQRCodeData } from "@/types";
import type { ScratchCardRewardBlock } from "@/api/warranty";
import { DEFAULT_TEMPLATE_ONE, KEY_LOCAL_STORAGE, TEMPLATE_TYPES } from "@/constants";

// Default values (keep existing...)
const defaultTemplate = {
  name: TEMPLATE_TYPES.TEMPLATE_1,
  code: TEMPLATE_TYPES.TEMPLATE_1,
  data: { ...DEFAULT_TEMPLATE_ONE },
};

const defaultCustomer: any = {
  name: "",
  phone: "",
};

const defaultStampCode: any = {
  verification_code: "",
  status: "",
  first_verification_at: "",
  message: "",
  has_warranty: false,
  stamp_has_warranty: false,
  warranty_starts_at: "",
  warranty_ends_at: "",
  is_show_verification_form: false,
  is_show_customer_stamp_anti_counterfeit: false,
};

const defaultProduct: IProduct = {
  price: 0,
  slug: "",
  note: "",
  image: "",
  images: [],
  unit: "",
  category: "",
  brand: "",
  name: "",
  description: "",
  custom_fields: [],
  is_show_product_price: false,
  related_products: [],
};

const defaultCompany: any = {
  name: "",
  address: "",
  phone: "",
  email: "",
  website: "",
  small_light_url: "",
  social_networks: [],
};

const defaultMessage: any = {
  logo: "",
  title: "",
  content: "",
};

const defaultParams: IParams = {
  xid: "",
  serial: "",
  ser: "",
  type: "",
  user_uuid: "",
  preview: false,
  utm: "",
  lat: "0",
  lon: "0",
};

interface ScanQrcodeState {
  isSerial: boolean;
  params: IParams;
  product: IProduct;
  company: ICompany;
  template: any;
  customer: any;
  stamp_code: any;
  verification_location: IIVerificationLocation | null;
  message: any;
  scratch_card_reward: ScratchCardRewardBlock | null;

  // Actions
  setIsSerial: (isSerial: boolean) => void;
  setParams: (params: IParams) => void;
  setStatusScanStampCode: (status: string) => void;
  setDataScanQrcode: (data: IScanQRCodeData) => void;
  setVerificationLocation: (location: IIVerificationLocation | null) => void;

  // Getters (computed values)
  getTemplateCode: () => string;
  getKeyThemeData: (key: string) => string; // 🆕 Add this
}

export const useScanQrcodeStore = create<ScanQrcodeState>((set, get) => ({
  isSerial: false,
  params: { ...defaultParams },
  product: { ...defaultProduct },
  company: { ...defaultCompany },
  template: { ...defaultTemplate },
  customer: { ...defaultCustomer },
  stamp_code: { ...defaultStampCode },
  verification_location: null,
  message: { ...defaultMessage },
  scratch_card_reward: null,

  setIsSerial: (isSerial: boolean) => {
    set({ isSerial });
  },

  setParams: (params: IParams) => {
    const newParams = { ...defaultParams, ...params };
    set({ params: newParams });
  },

  setStatusScanStampCode: (status: string) => {
    set((state) => ({
      stamp_code: { ...state.stamp_code, status },
    }));
  },

  setDataScanQrcode: (data: IScanQRCodeData) => {
    set({
      product: data?.product || data?.stamp_code?.product || { ...defaultProduct },
      company: data?.company || data?.stamp_code?.company || { ...defaultCompany },
      template: data?.template || { ...defaultTemplate },
      customer: data?.customer || data?.stamp_code?.customer || { ...defaultCustomer },
      stamp_code: data?.stamp_code || { ...defaultStampCode },
      verification_location: data?.verification_location || null,
      message: data?.message || { ...defaultMessage },
      scratch_card_reward: data?.scratch_card_reward || null,
    });

    // Handle localStorage like Vue project 🆕
    if (data?.template?.data) {
      window.localStorage.setItem(KEY_LOCAL_STORAGE.THEME_SETTING, JSON.stringify(data.template.data));
    }
    if (data?.company) {
      window.localStorage.setItem(KEY_LOCAL_STORAGE.COMPANY_SCAN, JSON.stringify(data.company));

      // Save language key to localStorage if available
      if (data.company.lang_key) {
        window.localStorage.setItem("i18nextLng", data.company.lang_key);
      }
    }
    if (data?.message) {
      // 🆕 Add message storage
      window.localStorage.setItem(KEY_LOCAL_STORAGE.STAMP_MESSAGE_STATES, JSON.stringify(data.message));
    }
  },

  setVerificationLocation: (location: IIVerificationLocation | null) => {
    set({ verification_location: location });
  },

  // Getters
  getTemplateCode: () => {
    const state = get();
    return state.template.code || TEMPLATE_TYPES.TEMPLATE_1;
  },

  // 🆕 Add getKeyThemeData function (like Vue project)
  getKeyThemeData: (key: string) => {
    const state = get();

    let themeSetting: any = {};
    let themeSettingData = window.localStorage.getItem(KEY_LOCAL_STORAGE.THEME_SETTING);

    if (themeSettingData === "undefined" || themeSettingData === "null" || !themeSettingData) {
      themeSettingData = "{}";
    }

    try {
      themeSetting = JSON.parse(themeSettingData || "{}");
    } catch (e) {
      console.log("Error parsing theme setting data", e);
    }

    const templateSettingStore: any = state.template.data;
    const templateDefault: any = DEFAULT_TEMPLATE_ONE;

    return themeSetting[key] ?? templateSettingStore[key] ?? templateDefault[key] ?? "";
  },
}));

// Setting Store (keep existing but add console logs)
interface SettingState {
  isFetchInit: boolean;
  setting: any;
  setSettingData: (setting: any) => void;
  actionIsFetched: () => void;
}

const defaultSetting: any = {
  icon_stamp_not_found_url: undefined,
  title_stamp_not_found: "",
  message_stamp_not_found: "",
  icon_stamp_error_url: undefined,
  title_stamp_error: "",
  message_stamp_error: "",
};

export const useSettingStore = create<SettingState>((set) => ({
  isFetchInit: false,
  setting: { ...defaultSetting },

  setSettingData: (setting: any) => {
    const newSetting = { ...defaultSetting };
    Object.keys(defaultSetting).forEach((key) => {
      newSetting[key] = setting[key] ?? undefined;
    });
    set({ setting: newSetting });
  },

  actionIsFetched: () => {
    set({ isFetchInit: true });
  },
}));
